<template>
  <div class="suggested-terms" v-if="selectedWidget.iconNouns && selectedWidget.iconNouns.length > 0">
    <label>Suggested: </label>
    <Chip
        v-for="term in selectedWidget.iconNouns"
        :key="term"
        :label="term"
        @click="handleChipClick(term)"
        class="mr-2"
        style="font-size: 14px; margin-right: 5px; background-color: #e3eaef;"
    />
  </div>
  <InputGroup>
    <InputText placeholder="Search Icons" v-model="searchQuery" @keyup.enter="requestIcons" />
    <Button icon="pi pi-search" severity="info" @click="requestIcons" />

    <div style="display:flex;align-items:center;background-color:#0288D1">
      <ColorButton v-tooltip.bottom="{ value: 'Icon Color', showDelay: 100, hideDelay: 100 }" :width="`21px`" :height="`21px`" :showColorBlock="true" :color="iconColor" @click="$refs.iconPickerVisible.toggle($event)"  />
      <OverlayPanel ref="iconPickerVisible">
        <ColorPicker
            :modelValue="iconColor"
            @update:modelValue="value => hexToFilter(value)"
        />
      </OverlayPanel>
    </div>
  </InputGroup>

  <div v-if="fetchingIcons">
    <div class="skeleton-grid">
      <div class="skeleton-item" v-for="index in 12" :key="index">
        <Skeleton size="100px" />
      </div>
    </div>
  </div>

  <div v-else-if="thumbnailUrls.length > 0">
    <div class="img-grid">
      <div class="img-item" v-for="url in thumbnailUrls" :key="url">
        <Image :src="url" alt="" @click="emitChosenFile(url)" :style="{ filter: filter }" />
      </div>
    </div>
  </div>

  <div v-else-if="hasSearched && !fetchingIcons && thumbnailUrls.length === 0">
    <p style="margin-top: 25px;font-size: 14px;">No results found. Try another search term.</p>
  </div>

</template>

<script setup lang="ts">
import {ref} from "vue";
import ColorButton from "@/views/Editor/Toolbar/common/ColorButton.vue";
import ColorPicker from "@/components/ColorPicker/index.vue";
import { hexToCSSFilter } from 'hex-to-css-filter';
import tinycolor from 'tinycolor2'
import {useWidgetStore} from "@/store";
import {storeToRefs} from "pinia";

const widgetStore = useWidgetStore()

const {selectedWidget} = storeToRefs(widgetStore)

const iconColor = ref('#525252')
const searchQuery = ref('')
const fetchingIcons = ref(false)
const hasSearched = ref(false)
const thumbnailUrls = ref([])
const filter = ref('')

const emit = defineEmits(['fileChosen', 'filterCss']);

const requestIcons = async () => {
  thumbnailUrls.value = []
  fetchingIcons.value = true
  hasSearched.value = false

  if (!filter.value) hexToFilter(iconColor.value)

  const response = await fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/file-service/stock-icons`, {
    method: 'POST',
    body: JSON.stringify({ query: searchQuery.value })
  })

  const data = await response.json()

  if (!data || data.icons.length === 0) {
    hasSearched.value = true
    fetchingIcons.value = false
  } else {
    for (let icon of data.icons) {
      thumbnailUrls.value.push(icon.thumbnail_url);
    }
    fetchingIcons.value = false
  }
}

const emitChosenFile = (url: string) => {
  emit('fileChosen', {
    url: url,
    width: null,
    height: null
  })
  const hex = tinycolor(iconColor.value).toHexString()
  emit('filterCss', { filter: filter.value, hex: hex })
}

//TODO: store this on the image widget
const hexToFilter = (hex: string) => {
  iconColor.value = hex
  const realHex = tinycolor(hex).toHexString()
  const cssFilter = hexToCSSFilter(realHex)
  filter.value = cssFilter.filter.replace(/;$/, '')
}

const handleChipClick = (term: string) => {
  searchQuery.value = term;
  requestIcons();
};

</script>

<style scoped lang="scss">
.skeleton-grid {
  padding-top: 20px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
}

.skeleton-item {
  display: flex;
  justify-content: center;
  overflow: hidden;
}

.img-grid {
  padding-top: 20px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
}

.img-item {
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: center;
  overflow: hidden;
}

:deep(.img-item > .p-image.p-component > img) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

:deep(.img-item > .p-image.p-component > img:hover) {
  cursor: pointer;
}

.suggested-terms {
  margin-bottom: 15px;
  cursor: pointer;
}

.suggested-terms label {
  margin-right: 5px;
}

</style>